import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Seleccionar",
  disabled = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((item) => String(item.id) === String(value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();
    if (!term) return options;

    return options.filter((item) =>
      String(item.nombre || "").toLowerCase().includes(term)
    );
  }, [options, search]);

  const calculatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;

    const estimatedHeight = 300;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setOpenUpward(shouldOpenUp);

    return {
      position: "fixed",
      left: rect.left,
      width: rect.width,
      top: shouldOpenUp ? "auto" : rect.bottom + gap,
      bottom: shouldOpenUp ? window.innerHeight - rect.top + gap : "auto",
      zIndex: 9999,
    };
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      if (
        !containerRef.current?.contains(target) &&
        !target.closest(".sz-searchable-dropdown-portal")
      ) {
        setOpen(false);
        setDropdownStyle(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (loading) {
      setOpen(false);
      setSearch("");
      setDropdownStyle(null);
    }
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDropdownStyle(null);
      return;
    }

    const updatePosition = () => {
      const style = calculatePosition();
      if (style) setDropdownStyle(style);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const handleSelect = (option) => {
    if (loading || disabled) return;

    onChange?.(option.id);
    setOpen(false);
    setSearch("");
    setDropdownStyle(null);
  };

  const handleToggle = () => {
    if (disabled || loading) return;

    if (open) {
      setOpen(false);
      setDropdownStyle(null);
      return;
    }

    const style = calculatePosition();
    if (!style) return;

    setDropdownStyle(style);
    setOpen(true);
  };

  const dropdown =
    open && !disabled && !loading && dropdownStyle
      ? createPortal(
          <div
            className={`sz-searchable-dropdown-portal ${
              openUpward ? "is-open-upward" : "is-open-downward"
            }`}
            style={dropdownStyle}
          >
            <div className="sz-searchable-input-wrap">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Escribe para buscar..."
                autoFocus
              />
            </div>

            <div className="sz-searchable-options">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={`sz-searchable-option ${
                      String(option.id) === String(value) ? "active" : ""
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.nombre}
                  </button>
                ))
              ) : (
                <div className="sz-searchable-empty">No hay resultados</div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className={`sz-searchable ${disabled ? "is-disabled" : ""}`}
        ref={containerRef}
      >
        <button
          ref={triggerRef}
          type="button"
          className={`sz-searchable-trigger ${open ? "is-open" : ""} ${loading ? "is-loading" : ""}`}
          onClick={handleToggle}
          disabled={disabled}
        >
          <span className={selectedOption ? "has-value" : "is-placeholder"}>
            {selectedOption?.nombre || placeholder}
          </span>

          {loading ? (
            <span className="sz-searchable-busy" aria-label="Cargando opciones" />
          ) : (
            <span className="sz-searchable-arrow">{open ? "▴" : "▾"}</span>
          )}
        </button>
      </div>

      {dropdown}
    </>
  );
}

export default SearchableSelect;